import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, ArrowLeft, Send, Check, Settings as SettingsIcon } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Inbox() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/inbox/conversations');
      setConversations(res.data.conversations || []);
    } catch (error) {
      toast.error(t('inbox.loadError'));
    }
  }, [t]);

  const fetchMessages = useCallback(async (phone) => {
    try {
      const res = await api.get(`/inbox/conversations/${phone}`);
      setMessages(res.data.messages || []);
      setSelectedContact(res.data.contact);
      
      // Auto mark as read
      if (res.data.messages.some(m => m.direction === 'in' && !m.read_by_owner)) {
        await api.post('/inbox/mark-read', { phone });
        await fetchConversations(); // Refresh unread counts
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [fetchConversations]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedPhone) return undefined;
    fetchMessages(selectedPhone);
    const interval = setInterval(() => fetchMessages(selectedPhone), 5000);
    return () => clearInterval(interval);
  }, [selectedPhone, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedPhone) return;

    setLoading(true);
    try {
      await api.post('/inbox/send', {
        phone: selectedPhone,
        text: newMessage.trim(),
      });
      
      setNewMessage('');
      await fetchMessages(selectedPhone);
      toast.success(t('inbox.messageSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('inbox.messageSendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleManualMode = async () => {
    if (!selectedPhone) return;

    try {
      const newMode = !selectedContact?.manual_mode;
      await api.patch('/inbox/manual-mode', {
        phone: selectedPhone,
        manual_mode: newMode,
      });
      
      toast.success(newMode ? t('inbox.manualModeActivated') : t('inbox.evaReactivated'));
      await fetchMessages(selectedPhone);
      await fetchConversations();
    } catch (error) {
      toast.error(t('inbox.modeChangeError'));
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('inbox.now');
    if (diffMins < 60) return t('inbox.minutesAgo', { n: diffMins });
    if (diffMins < 1440) return t('inbox.hoursAgo', { n: Math.floor(diffMins / 60) });
    return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
  };

  // Mobile: show list OR chat, not both
  const isMobile = window.innerWidth < 768;
  const showList = !isMobile || !selectedPhone;
  const showChat = !isMobile || selectedPhone;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="inbox-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {isMobile && selectedPhone && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPhone(null)}
                  data-testid="button-back-to-list"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <MessageSquare className="w-6 h-6 text-emerald-600" />
              <span className="text-xl font-bold text-slate-900">
                {selectedPhone ? selectedPhone : 'Inbox'}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <LanguageSelector />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="button-dashboard"
              >
                {t('common.dashboard')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                data-testid="button-logout"
              >
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-180px)]">
          {/* Conversation List */}
          {showList && (
            <div className={`${isMobile ? 'col-span-1' : 'col-span-1'} bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col`}>
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-slate-900">{t('inbox.conversations')}</h2>
                <p className="text-xs text-slate-500">
                  {conversations.length} {conversations.length === 1 ? t('inbox.contact') : t('inbox.contacts')}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('inbox.noConversations')}</p>
                  </div>
                ) : (
                  conversations.map((conv, idx) => (
                    <div
                      key={conv.phone}
                      onClick={() => setSelectedPhone(conv.phone)}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedPhone === conv.phone ? 'bg-emerald-50 border-l-4 border-l-emerald-600' : ''
                      }`}
                      data-testid={`conversation-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-medium text-slate-900 text-sm">{conv.phone}</span>
                        <span className="text-xs text-slate-500">{formatTime(conv.last_message_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600 truncate mb-2">{conv.last_message_snippet}</p>
                      <div className="flex items-center gap-2">
                        {conv.unread_count > 0 && (
                          <Badge variant="default" className="text-xs">
                            {conv.unread_count} {conv.unread_count > 1 ? t('inbox.unreadNewPlural') : t('inbox.unreadNew')}
                          </Badge>
                        )}
                        {conv.manual_mode && (
                          <Badge variant="warning" className="text-xs">{t('inbox.manualMode')}</Badge>
                        )}
                        {conv.language && (
                          <span className="text-xs text-slate-400">{conv.language.toUpperCase()}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Chat View */}
          {showChat && selectedPhone && (
            <div className={`${isMobile ? 'col-span-1' : 'col-span-2'} bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col`}>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedPhone}</h3>
                  {selectedContact && (
                    <p className="text-xs text-slate-500">
                      {selectedContact.language_override || t('languages.auto')} • {selectedContact.opted_out ? t('inbox.optedOut') : t('inbox.active')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleToggleManualMode}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      selectedContact?.manual_mode
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid="toggle-manual-mode"
                  >
                    {selectedContact?.manual_mode ? `👤 ${t('inbox.manualMode')}` : `🤖 ${t('inbox.eva')}`}
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-container">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">{t('inbox.noMessages')}</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.direction === 'out' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${idx}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-4 py-2 ${
                          msg.direction === 'out'
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-slate-900'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.has_media ? '[Media]' : msg.body}</p>
                        <p className={`text-xs mt-1 ${
                          msg.direction === 'out' ? 'text-emerald-100' : 'text-slate-500'
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString('de-CH', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-gray-200">
                {selectedContact?.manual_mode && (
                  <div className="mb-3 text-xs text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
                    {t('inbox.manualModeActive')}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    placeholder={t('inbox.typeMessage')}
                    disabled={loading}
                    data-testid="input-message"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={loading || !newMessage.trim()}
                    data-testid="button-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Empty state when no chat selected (desktop only) */}
          {!isMobile && !selectedPhone && (
            <div className="col-span-2 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('inbox.selectConversation')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
